/**
 * SN-4709 (Jeff, Sep 1) — the two answers the Review step's "What's Next" panel is
 * built around, kept OUT of the 1500-line screen so they can be tested.
 *
 * Both used to be inline expressions on a component with a ~30-field context, which is
 * why neither had a test and why both were wrong in ways nobody noticed.
 */
import { TEAM_BUDGET_PAYMENT_COPY } from '@sportninja/common/features/registration/teamBudgetPayment/constants/copy'
import {
  TEAM_REGISTRATION_SUBMITTED,
  type RepPaymentAction,
  type RepPaymentPresentation,
} from '@sportninja/common/features/registration/teamBudgetPayment/utils/repPaymentPresentation'

/**
 * RegistrationStatusType, mirrored. APPROVED (3) and APPROVED_WITH_CONDITIONS (4) are
 * the two the player-facing picker already treats as joinable, so they are also the two
 * that read as "approved" here.
 */
export const APPROVED_REGISTRATION_STATUSES = [3, 4]

export type ConfirmedTeam = {
  id?: string
  team_name?: string | null
  division_name?: string | null
  is_private?: boolean
  status?: number | null
  share_link?: string | null
} | null

/**
 * Whether the registration that was just created is approved.
 *
 * SERVER-DERIVED FIRST. The old code answered this from the competition's approval
 * CONFIGURATION, which was never the registration's actual state — only a prediction of
 * it — and which goes stale the moment a league approves a team by hand. With
 * auto-approve now split per registration type it is also ambiguous: reading the
 * agnostic flag tells a player "Pending League Approval" purely because TEAMS are
 * approved manually.
 *
 * The configuration remains the fallback, and it genuinely matters: a PLAYER
 * registration has no row on the session, so there is nothing else to go on.
 */
export const resolveIsApproved = (
  confirmedTeam: ConfirmedTeam,
  autoApproveFallback: boolean | undefined | null
): boolean => {
  const status = confirmedTeam?.status

  if (status !== undefined && status !== null) {
    return APPROVED_REGISTRATION_STATUSES.includes(Number(status))
  }

  return Boolean(autoApproveFallback)
}

/**
 * Pick the auto-approve flag for the registration type being reviewed.
 *
 * `auto_approve` is the fallback for an API that predates the split, where it is the one
 * true answer for both types.
 */
export const resolveAutoApproveForType = (
  config:
    | {
        auto_approve?: boolean
        auto_approve_team_registrations?: boolean
        auto_approve_player_registrations?: boolean
      }
    | null
    | undefined,
  isPlayerRegistration: boolean
): boolean | undefined => {
  const split = isPlayerRegistration
    ? config?.auto_approve_player_registrations
    : config?.auto_approve_team_registrations

  return split ?? config?.auto_approve
}

/**
 * The share link to offer, if any.
 *
 * TEAM registrations only: a player has no team of their own to share, and an empty
 * "Invite Players" card is worse than no card. Null whenever the server sent no link —
 * there is genuinely nothing to copy, and a disabled copy button would say less than
 * silence.
 */
export const resolveShareLink = (
  confirmedTeam: ConfirmedTeam,
  isPlayerRegistration: boolean
): string | null => {
  if (isPlayerRegistration) {
    return null
  }

  return confirmedTeam?.share_link ?? null
}

/* ------------------------------------------------------------------------- *
 * SN-8326 / SN-8344 — the Team-Managed Budget payment story.
 * ------------------------------------------------------------------------- */

/**
 * Which story the payment step (4) and the confirmation step (5) should tell.
 *
 * `paid` is NOT evidence that money changed hands. RegistrationConfirmationService:257
 * deliberately COMPLETES a zero-total session, and RegistrationBillingScheduleService:211
 * suppresses the initial invoice for a Team-Managed Budget division — so a TMB
 * registration reaches step 5 with `paid === true`, no charge, and no invoice. Reading
 * `paid` alone is exactly what put "Payment Approved" on a registration that has paid
 * nothing (SN-8326 issue 2, SN-8344).
 *
 * TMB is checked FIRST, ahead of `deferPayment`, because a TMB registrant is never
 * offered the deferred path (the payment step renders the zero-balance panel, which has
 * no method choice at all) — so a defer flag arriving alongside it is stale session
 * state, and "you have chosen to defer this payment" would be a second false statement.
 *
 * Presentation only. Nothing here changes what the server charges, and nothing is
 * charged or invoiced at registration time on this path.
 */
/** The pre-existing confirmation subtitle, unchanged for every non-TMB registration. */
export const LEGACY_CONFIRMATION_SUBTITLE =
  "Congratulations! Your account has been successfully verified. We've also emailed you the registration status and the receipt for your payment."

export type PaymentNarrative =
  | 'deferred'
  | 'team-managed-budget'
  | 'paid'
  | 'processing'

export const resolvePaymentNarrative = ({
  costDistributionEnabled,
  paid,
  deferPayment,
}: {
  costDistributionEnabled?: boolean | null
  paid?: boolean | null
  deferPayment?: boolean | null
}): PaymentNarrative => {
  if (costDistributionEnabled) {
    return 'team-managed-budget'
  }

  if (deferPayment) {
    return 'deferred'
  }

  return paid ? 'paid' : 'processing'
}

/**
 * The confirmation step's payment row — title, status, icon and description TOGETHER.
 *
 * They are returned as one object on purpose. They were four independent ternaries on
 * `paid`, so changing only the label would have left a green check and a "finish" state
 * beside a title that says nothing has been paid. `icon` is symbolic rather than a node
 * so this stays testable without rendering (client/CLAUDE.md:888).
 */
export type PaymentStepPresentation = {
  narrative: PaymentNarrative
  title: string
  status: 'finish' | 'process' | 'wait'
  icon: 'check' | 'clock'
  description: string
  /**
   * The confirmation screen's subtitle.
   *
   * It lives here rather than inline because the legacy copy promised "the receipt for
   * your payment" unconditionally — which is a second false statement on exactly the
   * registrations this predicate exists to correct: nothing was charged, so there is no
   * receipt to have sent.
   */
  subtitle: string
  /**
   * Sep 24 — only on a Team Budget rep's row once they chose to pay at registration:
   * PAY NOW / CONTINUE PAYMENT on their invoice.
   */
  action?: RepPaymentAction | null
  /** Sep 24 — the rep's row while the fresh session loads: a skeleton, no statement. */
  isLoading?: boolean
}

export const resolvePaymentStepPresentation = ({
  costDistributionEnabled,
  paid,
  deferPayment,
  isPlayerRegistration,
  repPayment,
  hasTeamBudgetHistory,
}: {
  costDistributionEnabled?: boolean | null
  paid?: boolean | null
  deferPayment?: boolean | null
  isPlayerRegistration?: boolean | null
  /**
   * Sep 24 — what the rep paid toward the deposit at registration
   * (resolveRepPaymentPresentation); null when they chose not to.
   */
  repPayment?: RepPaymentPresentation | null
  /**
   * F1 (SN-8441) — the TEAM session proves its rep paid (or was invoiced) at
   * registration: its Team Budget story stands after the division's flag is switched
   * off, and it never reads "Nothing has been charged today".
   */
  hasTeamBudgetHistory?: boolean | null
}): PaymentStepPresentation => {
  const teamHistory = !isPlayerRegistration && Boolean(hasTeamBudgetHistory)
  const narrative = resolvePaymentNarrative({
    costDistributionEnabled: costDistributionEnabled || teamHistory,
    paid,
    deferPayment,
  })

  if (narrative === 'team-managed-budget') {
    if (!isPlayerRegistration && repPayment) {
      return {
        narrative,
        title: repPayment.title,
        status: repPayment.status,
        icon: repPayment.icon,
        description: repPayment.description,
        subtitle: repPayment.subtitle,
        action: repPayment.action,
        isLoading: repPayment.isLoading,
      }
    }

    if (teamHistory) {
      // A payment exists but its details are not in this view: neutral, never "nothing charged".
      return {
        narrative,
        title: 'Payment Details Are On Your Team Budget',
        status: 'process',
        icon: 'clock',
        description:
          'Your Team Budget shows what has been paid toward the team fee.',
        subtitle: TEAM_REGISTRATION_SUBMITTED,
      }
    }

    return {
      narrative,
      // Neutral and pending — never a completed payment (SN-8344).
      title: isPlayerRegistration
        ? 'Awaiting Team Invoice'
        : 'Team Fee Pending — To Be Split Among Players',
      status: 'process',
      icon: 'clock',
      description: isPlayerRegistration
        ? "You'll receive your payment info once your Team Official splits the team cost and sends your invoice. You'll be able to pay it from 'My Registrations' in your account."
        : 'Your team fee will be split among your players and invoiced to them after registration. Nothing has been charged today.',
      // No receipt is promised: nothing was charged, so none exists.
      subtitle: isPlayerRegistration
        ? 'Congratulations! Your registration has been submitted. Nothing has been charged today — your Team Official will split the team cost and send your invoice.'
        : 'Congratulations! Your team registration has been submitted. Nothing has been charged today — the team fee will be split and invoiced to your players.',
    }
  }

  if (narrative === 'deferred') {
    return {
      narrative,
      title:
        'You have chosen to defer this payment. Please arrange payment with the league.',
      status: 'process',
      icon: 'clock',
      description: '',
      subtitle: LEGACY_CONFIRMATION_SUBTITLE,
    }
  }

  if (narrative === 'paid') {
    return {
      narrative,
      title: 'Payment Approved',
      status: 'finish',
      icon: 'check',
      description: '',
      subtitle: LEGACY_CONFIRMATION_SUBTITLE,
    }
  }

  return {
    narrative,
    title: 'Processing Payment',
    status: 'process',
    icon: 'clock',
    description: 'This Could Take A Few Minutes',
    subtitle: LEGACY_CONFIRMATION_SUBTITLE,
  }
}

/**
 * The payment step's zero-balance panel.
 *
 * The old copy — "your discount covers the full amount owed" — was written for a 100%
 * coupon (its own comment said so) and is simply false on a Team-Managed Budget
 * division: there is no discount, the fee is deferred to the players.
 *
 * `teamTotal` is the DIVISION's authoritative `team_cost`, never the session's pricing:
 * a TMB session prices at zero, so inferring a total from it would print $0.00 again.
 * It is stated as PRE-TAX because TMB is tax-on-top — each player is invoiced their
 * share plus applicable tax — so calling it an all-in total would understate what the
 * players are eventually billed.
 */
export type ZeroDueSummary = {
  message: string
  /** Pre-tax division team cost, or null when this zero balance is not TMB. */
  teamTotal: number | null
  /**
   * Shown in the zero-balance PANEL, under `message`.
   *
   * Deliberately disjoint from `receiptNote`: the panel and the receipt are siblings of
   * the same flex row and are on screen together, so one sentence used for both printed
   * itself twice on the team-rep path.
   */
  note: string | null
  /** Shown in the RECEIPT beside the team total. Never the same string as `note`. */
  receiptNote: string | null
}

export const resolveZeroDueSummary = ({
  costDistributionEnabled,
  isPlayerRegistration,
  teamCost,
}: {
  costDistributionEnabled?: boolean | null
  isPlayerRegistration?: boolean | null
  teamCost?: number | string | null
}): ZeroDueSummary => {
  if (!costDistributionEnabled) {
    return {
      message:
        'No payment is due today — your discount covers the full amount owed. Complete your registration below.',
      teamTotal: null,
      note: null,
      receiptNote: null,
    }
  }

  if (isPlayerRegistration) {
    return {
      message:
        'No payment is due today — your team is on a team-managed budget. Complete your registration below.',
      teamTotal: null,
      note: "Your Team Official will split the team fee and send your invoice. You'll be able to pay it from 'My Registrations' in your account.",
      receiptNote: null,
    }
  }

  // A team cost of zero is not a figure worth printing, and the division flag cannot be
  // enabled without one anyway — so "greater than zero" is the whole guard. It subsumes
  // null, undefined and any unparseable value.
  const parsed = Number(teamCost)

  return {
    message:
      'No payment is due today — the team fee will be split and invoiced to players after registration. Complete your registration below.',
    teamTotal: Number.isFinite(parsed) && parsed > 0 ? parsed : null,
    // The panel's own message already explains the split; only the receipt needs the
    // tax basis, beside the figure it qualifies.
    note: null,
    receiptNote:
      'Each player is invoiced their share plus applicable tax. Nothing is charged today.',
  }
}

export interface PaymentScreenHeading {
  title: string
  subtitle: string
}

/**
 * SN-8326 (R2-04) — what the PAYMENT step calls itself.
 *
 * The screen hardcoded "Pay Your Deposit" / "Please make a deposit payment to complete
 * your registration", which on a Team-Managed Budget division sat directly above a
 * $0.00 total and a panel explaining that nothing is collected today: the same screen
 * gave two contradictory instructions. On TMB nothing is payable here at all — the fee
 * is split and invoiced after registration — so the heading has to say that instead.
 *
 * Every non-TMB path keeps its exact previous wording: single-payment and pay-in-full
 * still read "Pay Your Registration", instalments still ask for the deposit.
 *
 * Sep 24 — a TMB team rep who is offered to pay toward the deposit here
 * (`offersDepositPayment`) is told so; "no payment is due today" would contradict the
 * choice right below it.
 */
export const resolvePaymentScreenHeading = ({
  costDistributionEnabled,
  isPlayerRegistration,
  isSinglePayment,
  isPayInFullSelected,
  offersDepositPayment,
}: {
  costDistributionEnabled?: boolean | null
  isPlayerRegistration?: boolean | null
  isSinglePayment?: boolean | null
  isPayInFullSelected?: boolean | null
  offersDepositPayment?: boolean | null
}): PaymentScreenHeading => {
  if (costDistributionEnabled) {
    if (!isPlayerRegistration && offersDepositPayment) {
      return {
        title: 'Complete Your Registration',
        subtitle: TEAM_BUDGET_PAYMENT_COPY.headingSubtitle,
      }
    }

    return {
      title: 'Complete Your Registration',
      subtitle: isPlayerRegistration
        ? 'No payment is due today — your Team Official will split the team fee and send you an invoice for your share.'
        : 'No payment is due today — the team fee is split and invoiced to your players after registration.',
    }
  }

  if (isSinglePayment || isPayInFullSelected) {
    return {
      title: 'Pay Your Registration',
      subtitle:
        'Please make a payment to complete your registration for the competition.',
    }
  }

  return {
    title: 'Pay Your Deposit',
    subtitle:
      'Please make a deposit payment to complete your registration for the competition.',
  }
}
